'use client';

import type { FC } from 'react';
import type { ModalNameType } from '@/modals/components/ModalProvider';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { RatingStars } from '@/components/shared/RatingStars';
import { Button } from '@/components/ui/Button/button';
import { reviewsQueryOptions } from '@/queries/reviews/review.queries';
import { cn } from '@/utils/cn';
import { ReviewSkeleton } from './ReviewSkeleton';

const formatDate = (date: string | Date) =>
  new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(date));

interface GameReviewProps {
  className?: string;
  gameId?: string;
}

export const GameReview: FC<GameReviewProps> = ({ className, gameId }) => {
  const t = useTranslations('game.game-details.reviews-section');

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } = useInfiniteQuery(
    reviewsQueryOptions.getByGameInfinite({
      gameId: gameId || '',
      params: { limit: 5 },
    }),
  );

  const searchParams = useSearchParams();

  if (!gameId) {
    return null;
  }

  const handleAddReview = () => {
    const params = new URLSearchParams(searchParams);
    params.set('gameId', gameId || '');

    if (typeof window !== 'undefined') {
      window.history.pushState({}, '', `?m=${'add-review' satisfies ModalNameType}&${params.toString()}`);
    }
  };

  const allReviews = data?.pages.flatMap(page => page.items) || [];

  if (allReviews.length === 0 && !isFetchingNextPage && !isLoading) {
    return null;
  }

  return (
    <section className={cn('flex flex-col gap-4 max-w-200', className)}>
      {/* <h3 className="mb-4 text-xl leading-[1.4] font-medium py-1.5 text-light">{t('reviews')}</h3> */}
      <div className="flex items-center justify-between">
        {/* TODO: add rating display, when it will be implemented */}

        <h3 className="text-xl leading-[1.4] font-medium py-1.5 text-light">{t('reviews')}</h3>

        <Button variant="secondary" size="sm" className="py-3 px-6" onClick={handleAddReview}>{t('review')}</Button>
      </div>

      <div className="w-full h-px bg-c600"></div>

      {allReviews && allReviews.length > 0
        ? (
            <div className="flex flex-col gap-4">
              {allReviews.map((review, index) => (
                <div
                  key={review.id}
                  className="flex gap-4 flex-col sm:flex-row items-start animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both"
                  style={{ animationDelay: `${(index % 2) * 100}ms` }}
                >
                  <RatingStars interactive={false} showValue={false} rating={review.rating} maxRating={5} size="lg" />
                  <div className="wrap-break-word">
                    <p className="text-light text-base leading-normal wrap-break-word font-medium mb-1">{review.username}</p>
                    <p className="text-c300 text-base leading-normal wrap-break-word mb-2.5">{review.comment}</p>
                    <p className="text-c300 text-xs leading-[1.3] font-medium">{formatDate(review.createdAt)}</p>
                  </div>
                </div>
              ))}
              {isFetchingNextPage && (
                <div className="flex flex-col gap-4">
                  <ReviewSkeleton />
                  <ReviewSkeleton />
                </div>
              )}
              {hasNextPage && (
                <div className="flex justify-center mt-4">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="py-3 px-6"
                    onClick={() => fetchNextPage()}
                    isLoading={isFetchingNextPage}
                  >
                    {t('show-more-reviews') || 'Show More'}
                  </Button>
                </div>
              )}
            </div>
          )
        : (
            <p className="text-c300 text-base">{t('default-descriptions')}</p>
          )}
    </section>
  );
};
